// IndexedDB service for requirement gathering persistence
interface RequirementGatheringState {
    projectId: string;
    messages: any[];
    currentStep: string;
    selectedAgent: string | null;
    selectedMode: string | null;
    workflowInitialized: boolean;
    sessionId: string | null;
    lastUpdated: number;
}

const DB_NAME = 'SuperEngineerDB';
const STORE_NAME = 'requirementGathering';
const DB_VERSION = 1;

class RequirementGatheringDB {
    private db: IDBDatabase | null = null;

    async init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'projectId' });
                }
            };
        });
    }

    async saveState(state: RequirementGatheringState): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.put({ ...state, lastUpdated: Date.now() });

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    async loadState(projectId: string): Promise<RequirementGatheringState | null> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(projectId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    async clearState(projectId: string): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(projectId);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
}

export const requirementGatheringDB = new RequirementGatheringDB();
export type { RequirementGatheringState };
